import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  FormControl,
  Input,
  Button,
  InputGroup,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  Avatar,
  InputRightElement,
  Image,
} from "@chakra-ui/react";

import { useForm } from "react-hook-form";
import {
  InputControl,
  RadioControl,
  SelectControl,
} from "../../../components/form/formControls";
import NonAvatar from "../../../components/Icons/avartar";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import calendarIcon from "../../../assets/images/onleave/calendar.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import vi from "date-fns/locale/vi";
import { DataPlaceOfIdentification } from "../../../utils/globalVariable";
import { relativeFormRequestCall } from "../../../api/relativeFormRequest";
import toast from "react-hot-toast";

registerLocale("vi", vi);

export const FormEdit = memo(
  ({ isOpen, onClose, defaultValues, onSubmit, refetch }) => {
    const schema = Yup.object().shape({
      fullName: Yup.string().required("Vui lòng nhập họ tên"),
      phone: Yup.string()
        .required("Vui lòng nhập số điện thoại")
        .matches(/^[0-9]{10,11}$/, "Số điện thoại không hợp lệ."),
      email: Yup.string()
        .required("Vui lòng nhập email")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email không hợp lệ"),

      identificationNumber: Yup.string()
        .test(
          "is-valid-id-number",
          "Số CCCD/CMND không hợp lệ (9 hoặc 12 số).",
          (value) => {
            if (!value) {
              return true;
            }
            return /^\d{9}$|^\d{12}$/.test(value);
          }
        )
        .nullable(),
      dateOfIdentification: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Thời gian cấp không hợp lệ."),
      startTime: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Thời gian bắt đầu không hợp lệ."),
    });

    const {
      register,
      getValues,
      handleSubmit,
      setValue,
      clearErrors,
      setError,
      watch,
      reset,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(schema),
      defaultValues: defaultValues || {},
    });

    const genders = [
      {
        value: 0,
        label: "Nam",
      },
      {
        value: 1,
        label: "Nữ",
      },
      {
        value: 2,
        label: "Khác",
      },
    ];

    useEffect(() => {
      if (isOpen) {
        reset({
          ...defaultValues,
        });
      }
    }, [isOpen, defaultValues, reset]);

    const [showStartTime, setShowStartTime] = useState(false);
    const randomWeekdayTime = async () => {
      const data = await relativeFormRequestCall.randomWeekdayTime();
      if (data?.success) {
        setShowStartTime(data?.success);
      }
    };
    useEffect(() => {
      randomWeekdayTime();
    }, [showStartTime]);

    const onchangeGender = (option) => {
      setValue("gender", option);
    };

    const [zIndex, setZIndex] = useState("");
    const [focusStates, setFocusStates] = useState({});

    const handleFocus = (field, data) => {
      if (data) {
        setZIndex(data);
      }
      setFocusStates((prev) => ({ ...prev, [field]: true }));
    };

    const handleBlur = (field, data) => {
      setZIndex("");
      setFocusStates((prev) => ({ ...prev, [field]: false }));
    };

    const handleDateChange = (selectedDate, inputName) => {
      setValue(inputName, selectedDate, { shouldValidate: true });
    };

    const submitHandler = (data) => {
      onSubmit(data);
    };

    const handleEdit = async (id) => {
      const toastEdit = toast.loading("Đang cập nhập trạng thái ...");
      const dataFormat = {
        status: "REJECTED",
      };

      try {
        await relativeFormRequestCall.approve(id, dataFormat);
        await refetch();
        toast.success("Cập nhập trạng thái thành công");
      } catch (error) {
        console.error("Error:", error);
        toast.error("Có lỗi xảy ra khi cập nhập trạng thái");
      }
      toast.dismiss(toastEdit);
      onClose();
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        // motionPreset="fade"
      >
        <ModalOverlay />
        <ModalContent
          backgroundColor="var(--color-main)"
          borderRadius={"16px"}
          padding={"24px"}
          h={"86.5vh"}
          w={"100%"}
          maxW={"625px"}
          overflow="auto"
        >
          <form onSubmit={handleSubmit(submitHandler)}>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"10px"}
            >
              <Text
                color="var(--color-header-employee)"
                fontSize={"18px"}
                fontWeight={"600"}
              >
                {"Thông tin cộng tác viên"}
              </Text>
              <Box cursor={"pointer"} onClick={onClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="#99A4B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15 9L9 15"
                    stroke="#99A4B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 9L15 15"
                    stroke="#99A4B8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </Flex>
            <Box
              w={"100%"}
              h={"1px"}
              backgroundColor={"var(--bg-line-employee)"}
              marginTop={"26px"}
              marginBottom={"24px"}
            ></Box>
            <Box w={"100%"} maxW={"545px"} m={"0 auto"}>
              {/* Khooi 1 */}
              <Box mt={"20px"}>
                <Text
                  fontSize={"16px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"16px"}
                >
                  Thông tin cá nhân
                </Text>
                {/* 1 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <InputControl
                    label="Họ và tên"
                    placeholder="Nhập họ và tên"
                    errors={errors.fullName}
                    register={register("fullName")}
                    onFocus={() => handleFocus("fullName")}
                    onBlur={() => handleBlur("fullName")}
                    state={focusStates["fullName"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                </Flex>
                {/* 1.2 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <FormControl zIndex={zIndex === "dateOfBirth" ? 3 : 0}>
                    <FormLabel
                      mb={"6px"}
                      fontSize={{ base: "12px" }}
                      fontWeight={400}
                      color={
                        focusStates["dateOfBirth"]
                          ? "var(--color-option-employee-hover)"
                          : "var(--color-label-employee)"
                      }
                    >
                      Ngày sinh
                    </FormLabel>
                    <InputGroup>
                      <DatePicker
                        width="100%"
                        selected={watch("dateOfBirth")}
                        onChange={(date) =>
                          handleDateChange(date, "dateOfBirth")
                        }
                        dateFormat="dd/MM/yyyy"
                        locale="vi"
                        placeholderText="dd/mm/yy"
                        className="custom-datepicker datePickerStyle"
                        onFocus={() =>
                          handleFocus("dateOfBirth", "dateOfBirth")
                        }
                        onBlur={() => handleBlur("dateOfBirth")}
                      />
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    </InputGroup>
                    {errors.dateOfBirth && (
                      <Text fontSize={{ base: "12px" }} color={"red"}>
                        {errors.dateOfBirth.message}
                      </Text>
                    )}
                  </FormControl>
                  <SelectControl
                    label="Giới tính"
                    dataOption={genders}
                    selectedMain={watch("gender")}
                    placeholder="Chọn giới tính"
                    onChange={onchangeGender}
                    onFocus={() => handleFocus("gender")}
                    onBlur={() => handleBlur("gender")}
                    state={focusStates["gender"]}
                    registerProps={register("gender")}
                  />
                </Flex>
              </Box>
              {/* Khooi 2 */}
              {!showStartTime && (
                <Box mt={"20px"}>
                  <Text
                    fontSize={"16px"}
                    lineHeight={"19px"}
                    fontWeight={"600"}
                    color={"var(--color-header-employee)"}
                    mb={"16px"}
                  >
                    Hoạt động
                  </Text>
                  <Flex
                    justifyContent={"space-between"}
                    alignItems={"flex-start"}
                    gap={"27px"}
                  >
                    <FormControl zIndex={zIndex === "startTime" ? 3 : 0}>
                      <FormLabel
                        mb={"6px"}
                        fontSize={{ base: "12px" }}
                        fontWeight={400}
                        color={
                          focusStates["startTime"]
                            ? "var(--color-option-employee-hover)"
                            : "var(--color-label-employee)"
                        }
                      >
                        Ngày vào làm
                      </FormLabel>
                      <InputGroup>
                        <DatePicker
                          width="100%"
                          selected={watch("startTime")}
                          onChange={(date) =>
                            handleDateChange(date, "startTime")
                          }
                          dateFormat="dd/MM/yyyy"
                          locale="vi"
                          placeholderText="dd/mm/yy"
                          className="custom-datepicker datePickerStyle"
                          onFocus={() => handleFocus("startTime", "startTime")}
                          onBlur={() => handleBlur("startTime")}
                        />
                        <InputRightElement pointerEvents="none" h={"44px"}>
                          <Image src={calendarIcon} />
                        </InputRightElement>
                      </InputGroup>
                      {errors.startTime && (
                        <Text fontSize={{ base: "12px" }} color={"red"}>
                          {errors.startTime.message}
                        </Text>
                      )}
                    </FormControl>
                  </Flex>
                </Box>
              )}
              {/* Khooi 4 */}
              <Box mt={"20px"}>
                <Text
                  fontSize={"16px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"16px"}
                >
                  Thông tin liên hệ
                </Text>
                {/* 1 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <InputControl
                    type="number"
                    label="Số điện thoại"
                    placeholder="Nhập số điện thoại"
                    errors={errors.phone}
                    register={register("phone")}
                    onFocus={() => handleFocus("phone")}
                    onBlur={() => handleBlur("phone")}
                    state={focusStates["phone"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                  <InputControl
                    type="email"
                    label="Email"
                    placeholder="Nhập email"
                    errors={errors.email}
                    register={register("email")}
                    onFocus={() => handleFocus("email")}
                    onBlur={() => handleBlur("email")}
                    state={focusStates["email"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                </Flex>
                {/* 2 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                >
                  <InputControl
                    label="Chỗ ở hiện tại"
                    placeholder="Nhập chỗ ở hiện tại"
                    // errors={errors.currentAddress}
                    register={register("currentAddress")}
                    onFocus={() => handleFocus("currentAddress")}
                    onBlur={() => handleBlur("currentAddress")}
                    state={focusStates["currentAddress"]}
                    mwidth={"100%"}
                  />
                </Flex>
              </Box>
              {/* Khooi 5 */}
              <Box mt={"20px"}>
                <Text
                  fontSize={"16px"}
                  lineHeight={"19px"}
                  fontWeight={"600"}
                  color={"var(--color-header-employee)"}
                  mb={"16px"}
                >
                  Thông tin giấy tờ
                </Text>
                {/* 1 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <InputControl
                    type="number"
                    label="Số CCCD/CMND"
                    placeholder="Vui lòng nhập CCCD"
                    errors={errors.identificationNumber}
                    register={register("identificationNumber")}
                    onFocus={() => handleFocus("identificationNumber")}
                    onBlur={() => handleBlur("identificationNumber")}
                    state={focusStates["identificationNumber"]}
                    mwidth={"100%"}
                  />
                  <FormControl
                    w={"100%"}
                    maxW={"259px"}
                    zIndex={zIndex === "dateOfIdentification" ? 3 : 0}
                  >
                    <FormLabel
                      mb={"6px"}
                      fontSize={{ base: "12px" }}
                      lineHeight={"15px"}
                      fontWeight={400}
                      color={
                        focusStates["dateOfIdentification"]
                          ? "var(--color-option-employee-hover)"
                          : "var(--color-label-employee)"
                      }
                    >
                      Ngày cấp
                    </FormLabel>
                    <InputGroup>
                      <DatePicker
                        width="100%"
                        selected={watch("dateOfIdentification")}
                        onChange={(date) =>
                          handleDateChange(date, "dateOfIdentification")
                        }
                        dateFormat="dd/MM/yyyy"
                        locale="vi"
                        placeholderText="dd/mm/yy"
                        className="custom-datepicker datePickerStyle"
                        onFocus={() =>
                          handleFocus(
                            "dateOfIdentification",
                            "dateOfIdentification"
                          )
                        }
                        onBlur={() => handleBlur("dateOfIdentification")}
                      />
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    </InputGroup>
                    {errors.dateOfIdentification && (
                      <Text fontSize={{ base: "12px" }} color={"red"}>
                        {errors.dateOfIdentification.message}
                      </Text>
                    )}
                  </FormControl>
                </Flex>
                {/* 2 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                  mb={"12px"}
                >
                  <InputControl
                    label="Nơi cấp"
                    placeholder="Nhập nơi cấp..."
                    // errors={errors.placeOfIdentification}
                    register={register("placeOfIdentification")}
                    onFocus={() => handleFocus("placeOfIdentification")}
                    onBlur={() => handleBlur("placeOfIdentification")}
                    state={focusStates["placeOfIdentification"]}
                    onChange={(e) =>
                      setValue("placeOfIdentification", e.target.value)
                    }
                    data={DataPlaceOfIdentification}
                    mwidth={"100%"}
                  />
                  <InputControl
                    label="Địa chỉ thường trú"
                    placeholder="Nhập địa chỉ thường trú..."
                    // errors={errors.permanentAddress}
                    register={register("permanentAddress")}
                    onFocus={() => handleFocus("permanentAddress")}
                    onBlur={() => handleBlur("permanentAddress")}
                    state={focusStates["permanentAddress"]}
                    mwidth={"100%"}
                  />
                </Flex>
                {/* 3 */}
                <Flex
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  gap={"27px"}
                >
                  <InputControl
                    label="Mã số thuế"
                    placeholder="Nhập mã số thuế"
                    register={register("taxCode")}
                    onFocus={() => handleFocus("taxCode")}
                    onBlur={() => handleBlur("taxCode")}
                    state={focusStates["taxCode"]}
                    // mwidth={"100%"}
                  />
                </Flex>
              </Box>
            </Box>
            <Flex
              justifyContent={"flex-end"}
              mt={"24px"}
              w={"100%"}
              // maxW={"545px"}
              gap={4}
            >
              <Button
                type="submit"
                borderRadius={"8px"}
                background={"var(--linear-gradient-employee)"}
                color={"var(--color-main)"}
                w={"164px"}
                h={"44px"}
                _hover={{
                  filter: "brightness(90%)",
                }}
              >
                {"Duyệt"}
              </Button>
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (defaultValues?.id) {
                    handleEdit(defaultValues?.id);
                  }
                }}
                borderRadius={"8px"}
                bg={"var(--color-main)"}
                color={"var(--color-option-employee-hover)"}
                border={"1px solid var(--color-option-employee-hover)"}
                w={"164px"}
                h={"44px"}
                _hover={{
                  filter: "brightness(90%)",
                }}
              >
                {"Không duyệt"}
              </Button>
            </Flex>
          </form>
        </ModalContent>
      </Modal>
    );
  }
);

export default FormEdit;
